import { apiFetch } from './apiFetch';

export interface Store {
  id: string;
  name: string | null;
  slug: string | null;
  refCode: string | null;
  timezone: string | null;
}

interface RawStore extends Omit<Store, 'timezone'> {
  metadata?: { timezone?: string | null } | null;
}

const toStore = (raw: RawStore): Store => ({
  id: raw.id,
  name: raw.name,
  slug: raw.slug,
  refCode: raw.refCode,
  timezone: raw.metadata?.timezone ?? null,
});

interface GetStoresResponse {
  stores: RawStore[];
}

/**
 * Fetches the stores the authenticated user is assigned to manage.
 * Used to populate the store picker for store-scoped briefings/suggested actions.
 */
export const getAssignedStores = async (): Promise<Store[]> => {
  const res = await apiFetch(`/stores?assigned=true`, {
    method: 'GET',
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err?.error ?? res.statusText);
  }

  const body = (await res.json()) as GetStoresResponse;
  return (body.stores ?? []).map(toStore);
};

export interface StoreSettingsPatch {
  timezone: string;
}

/**
 * Sets a user-configurable store setting (currently just `timezone`). Manager-only
 * on the backend, and `timezone` is first-write-wins: once set, it cannot be changed
 * through this endpoint.
 */
export const patchStoreSettings = async (storeId: string, patch: StoreSettingsPatch): Promise<Store> => {
  const res = await apiFetch(`/stores/${storeId}/settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err?.error ?? res.statusText);
  }

  return toStore((await res.json()) as RawStore);
};
